import { faChevronDown, faChevronRight, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { DateTimeField } from '~/team_tools/components/DateTimeField'
import { showInZone, useZone } from '~/team_tools/components/timezone'
import { Window, deleteWindow, getTemplates, saveWindow } from './templatesApi'

/**
 * Servers that are going to exist, and when.
 *
 * A schedule set on the Make servers tab lands here, above the deleted
 * ones, so the board reads top to bottom as: what is running, what is
 * coming, what is gone. Each line says what it builds, when, whether it
 * tears down after, whether it repeats, and when it last fired. Switch one
 * off to park it; delete removes it.
 */
/** "2h 14m", "38m", "under a minute". */
const left = (ms: number) => {
    if (ms <= 60_000) return 'under a minute'
    const minutes = Math.floor(ms / 60_000)
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return h ? `${h}h ${m}m` : `${m}m`
}

/** A schedule whose servers are up right now: which servers, how long until
 *  they are destroyed, and a way to move that.
 *
 *  The time edited is THIS run's teardown. The schedule stores its teardown as
 *  an offset from its first build, so the change is applied as a shift to that
 *  -- which for a weekly schedule moves every later week's teardown by the
 *  same amount, and the line says so. */
const LiveRun = ({ w, canEdit, onSaved }: { w: Window; canEdit: boolean; onSaved: () => void }) => {
    const { addNotification } = useNotification()
    const [now, setNow] = useState(Date.now())
    const [draft, setDraft] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [zone] = useZone()
    // Ticks once a minute: the countdown is in minutes.
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30_000)
        return () => clearInterval(t)
    }, [])

    const live = w.live!
    const endsAt = live.ends_at

    const moveTo = async (iso: string) => {
        if (!endsAt || !w.destroy_at) return
        const shift = new Date(iso).getTime() - new Date(endsAt).getTime()
        if (new Date(iso).getTime() <= Date.now()) {
            addNotification('Pick a time in the future.', 'error')
            return
        }
        setSaving(true)
        try {
            await saveWindow(w.id, {
                destroy_at: new Date(new Date(w.destroy_at).getTime() + shift).toISOString(),
            })
            addNotification(`"${w.name}" now tears down ${showInZone(iso, zone)}`, 'success')
            setDraft(null)
            onSaved()
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setSaving(false)
        }
    }
    const extend = (hours: number) =>
        endsAt && moveTo(new Date(new Date(endsAt).getTime() + hours * 3_600_000).toISOString())

    return (
        <div className='flex flex-col gap-1 rounded border border-green-500/30 bg-green-500/[0.04] px-3 py-2'>
            <div className='flex flex-row flex-wrap items-center gap-x-3 gap-y-1'>
                <span className='text-[10px] font-bold uppercase tracking-widest text-green-400'>live</span>
                <span className='font-bold'>
                    {live.servers.length
                        ? live.servers.map((sv) => sv.name).join(', ')
                        : `${w.name} (servers still starting)`}
                </span>
                <span className='ml-auto tabular-nums'>
                    {endsAt ? (
                        <>
                            <span className='text-white/40'>destroyed in </span>
                            <span className='font-bold text-amber-300'>
                                {left(new Date(endsAt).getTime() - now)}
                            </span>
                            <span className='text-white/40'> · {showInZone(endsAt, zone)}</span>
                        </>
                    ) : (
                        <span className='text-amber-300'>no tear-down set</span>
                    )}
                </span>
            </div>
            {endsAt && canEdit && (
                <div className='flex flex-row flex-wrap items-center gap-2 text-xs'>
                    <span className='text-white/40'>tear down at</span>
                    <DateTimeField value={draft ?? endsAt} onChange={(v) => setDraft(v)} />
                    {draft && draft !== endsAt && (
                        <ActionButton onClick={async () => moveTo(draft)} disabled={saving} className='text-green-400'>
                            save
                        </ActionButton>
                    )}
                    <ActionButton onClick={async () => extend(1)} disabled={saving} className='text-white/70'>
                        +1h
                    </ActionButton>
                    <ActionButton onClick={async () => extend(3)} disabled={saving} className='text-white/70'>
                        +3h
                    </ActionButton>
                    {w.repeat_weekly && (
                        <span className='text-[10px] text-white/35'>
                            weekly schedule: later weeks move by the same amount
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}

export const ScheduledServers = ({ canEdit }: { canEdit: boolean }) => {
    const { addNotification } = useNotification()
    const [open, setOpen] = useState(true)
    const [windows, setWindows] = useState<Window[] | null>(null)
    const [confirming, setConfirming] = useState<Window | null>(null)
    const [zone] = useZone()

    const load = useCallback(async () => {
        try {
            const d = await getTemplates()
            setWindows(d.windows ?? [])
        } catch {
            setWindows([])
        }
    }, [])

    // Read on mount and after a change, not on the board's 30s poll: a
    // schedule changes when somebody changes it, not on its own.
    useEffect(() => {
        load()
    }, [load])

    const toggle = async (w: Window) => {
        try {
            await saveWindow(w.id, { enabled: !w.enabled })
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            load()
        }
    }

    const remove = async (w: Window) => {
        setConfirming(null)
        try {
            await deleteWindow(w.id)
            addNotification(`Schedule "${w.name}" deleted`, 'success')
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            load()
        }
    }

    if (!windows || windows.length === 0) return null

    const upcoming = [...windows].sort((a, b) => new Date(a.deploy_at).getTime() - new Date(b.deploy_at).getTime())

    return (
        <div className='flex flex-col gap-2 rounded border border-violet-400/20 p-2'>
            <button
                type='button'
                onClick={() => setOpen((v) => !v)}
                className='flex w-full flex-row items-center gap-2 text-left'
            >
                <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className='w-3 text-white/40' />
                <span className='text-[10px] uppercase tracking-widest text-violet-300/80'>Scheduled servers</span>
                <span className='tabular-nums text-white/30'>{windows.length}</span>
                <span className='ml-auto text-[10px] text-white/30'>times in {zone}</span>
            </button>

            {open && (
                <div className='flex flex-col gap-1'>
                    {upcoming.map((w) =>
                        w.live ? (
                            <LiveRun key={w.id} w={w} canEdit={canEdit} onSaved={load} />
                        ) : (
                        <div
                            key={w.id}
                            className={`grid grid-cols-[minmax(0,1fr),11rem,11rem,5rem,9rem,7rem] items-center gap-3 rounded border px-3 py-1.5 ${
                                w.enabled ? 'border-white/10' : 'border-white/5 opacity-50'
                            }`}
                        >
                            <div className='flex min-w-0 flex-col'>
                                {/* Just the servers' names. The schedule's
                                    own name and the template summary repeated
                                    each other and the times beside them. */}
                                <span className='truncate font-bold' title={w.name}>
                                    {(w.server_names ?? []).length ? w.server_names.join(', ') : w.name}
                                    {!w.enabled && (
                                        <span className='ml-2 font-normal text-red-400'>off — will not run</span>
                                    )}
                                </span>
                            </div>
                            <span className='text-white/70'>
                                <span className='text-[10px] uppercase tracking-widest text-white/30'>builds </span>
                                {showInZone(w.deploy_at, zone)}
                            </span>
                            {/* A tear-down before the build is impossible to
                                create today -- `DeploymentWindow.clean`
                                refuses it and the save endpoint calls
                                `full_clean` -- but rows predating that guard
                                exist, and the board rendered them as a plain
                                pair of times that simply read as wrong. Said
                                out loud, because "builds 10:25, tears down
                                00:41" is otherwise a thing somebody has to
                                work out for themselves and then doubt. */}
                            <span
                                className={
                                    w.destroy_at && w.destroy_at <= w.deploy_at
                                        ? 'text-red-400'
                                        : 'text-white/70'
                                }
                            >
                                <span className='text-[10px] uppercase tracking-widest text-white/30'>tears down </span>
                                {w.destroy_at ? (
                                    <>
                                        {showInZone(w.destroy_at, zone)}
                                        {w.destroy_at <= w.deploy_at && (
                                            <span
                                                className='pl-1 text-[10px] uppercase tracking-widest'
                                                title='This schedule tears down before it builds, so it can never do anything. It predates the check that now refuses this; edit it or delete it.'
                                            >
                                                before it builds
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <span className='text-amber-300'>never</span>
                                )}
                            </span>
                            <span className='text-white/40'>{w.repeat_weekly ? 'weekly' : 'once'}</span>
                            <span className='truncate text-white/30'>
                                {w.last_deployed_at
                                    ? `last ran ${showInZone(w.last_deployed_at, zone)}`
                                    : 'has not run yet'}
                            </span>
                            <div className='flex flex-row justify-end gap-1'>
                                <ActionButton
                                    onClick={async () => toggle(w)}
                                    disabled={!canEdit}
                                    className={w.enabled ? 'text-green-400' : 'text-white/40'}
                                >
                                    {w.enabled ? 'on' : 'off'}
                                </ActionButton>
                                <ActionButton
                                    onClick={async () => setConfirming(w)}
                                    disabled={!canEdit}
                                    className='text-red-500'
                                    title='Delete this schedule'
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </ActionButton>
                            </div>
                        </div>
                        )
                    )}
                    {confirming && (
                        <ConfirmBar
                            message={`Delete the schedule "${confirming.name}"?`}
                            detail='Servers it already built stay. It just will not build any more.'
                            confirmLabel='Delete schedule'
                            onCancel={() => setConfirming(null)}
                            onConfirm={() => remove(confirming)}
                        />
                    )}
                </div>
            )}
        </div>
    )
}
